/*
 * Google Maps utilities used by RSP
 */
import { render } from 'react-dom';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { ACTIVE_ASSAILANT, TRAIN_DERAILMENT } from '@rsos/constants/alertTypes';
import safeCall from '@rsos/utils/safeCall';

/**
 * Creates a google.maps.Marker instance
 * @param {Object} markerOpts - options to create the instance with
 */
export const createGoogleMarker = (markerOpts = {}) =>
  new window.google.maps.Marker(markerOpts);
/**
 * Creates a google.maps.Circle instance
 * @param {Object} markerCircleOpts - options to create the instance with
 */
export const createGoogleMarkerCircle = (markerCircleOpts = {}) =>
  new window.google.maps.Circle(markerCircleOpts);
/**
 * Creates a google.maps.InfoWindow instance
 * @param {Object} infoWindowOpts - options to create the instance with
 */
export const createGoogleInfoWindow = (infoWindowOpts = {}) =>
  new window.google.maps.InfoWindow(infoWindowOpts);

/**
 * Remove all event handlers from a Google Maps object instance
 * Docs: https://developers.google.com/maps/documentation/javascript/events
 * @param {google.maps.Object} instance - An instance of any Google Maps type
 * @param {?String} eventName - One of ['click', 'mouseover', 'mouseout'] event names
 */
export const clearInstanceListeners = (instance, eventName) => {
  try {
    if (eventName) {
      // Clear instance listener for the event name
      window.google.maps.event.clearListeners(instance, eventName);
    } else {
      // Clear all instance listeners
      window.google.maps.event.clearInstanceListeners(instance);
    }
  } catch {
    /*
     Intentionally ignoring logging this error since it can potentially
     spam Sentry and adds no real debugging value. Try-catching to be
     safe since this is a 3rd-party API call.
     */
  }
};

/**
 * Renders the given React component into a google.maps.InfoWindow's DOM element.
 * @param {string} id - The ID for the InfoWindow's placeholder element in the DOM
 * @param {google.maps.InfoWindow} infoWindow - An instance of InfoWindow
 * @param {React.Component} component - An instance of a react component
 */
export const renderIntoInfoWindow = safeCall((id, infoWindow, component) => {
  // Make sure to execute once per call of this function. Otherwise, the
  // handlers registered will pile up indefinitely.
  window.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
    render(component, document.getElementById(id));
  });
  return infoWindow;
});

/**
 * Renders the given React component into a google.maps.InfoWindow's DOM element.
 * @param {string} id - The ID for the InfoWindow's placeholder element in the DOM
 * @param {google.maps.InfoWindow} infoWindow - An instance of InfoWindow
 * @param {React.Component} component - An instance of a react component
 */
export const renderIntoExistingInfoWindow = safeCall(
  (id, infoWindow, component) => {
    render(component, document.getElementById(id));
    return infoWindow;
  },
);

/**
 * Reverse geocode with Google Maps API. Returns a Promise with the results
 * @param {Object} latlng object that contains { lat, lng }
 */
export const googleReverseGeocode = latlng => {
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: latlng }, (result, status) => {
      if (status === 'OK') {
        resolve(result);
      } else {
        reject(result);
      }
    });
  });
};

/**
 * Geocode with Google Maps API. Returns a Promise with the results
 * @param {string} address - The address to be geocoded
 * @param {Object} bounds - Optional, the bounds to geocode within
 */
export const googleGeocode = (address, bounds) => {
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    const config = { address };
    if (bounds) {
      // bounds is an object that has { northeast: { lat, lng }, southwest: { lat, lng } }
      // make it a google.maps.LatLngBounds.
      config.bounds = new window.google.maps.LatLngBounds(
        bounds.southwest,
        bounds.northeast,
      );
    }
    geocoder.geocode(config, (result, status) => {
      if (status === 'OK') {
        resolve(result);
      } else {
        reject(result);
      }
    });
  });
};

export const getIsUnacceptedActiveAssailant = alert => {
  return (
    alert?.emergency_type?.name === ACTIVE_ASSAILANT &&
    alert?.status?.name !== ACCEPTED
  );
};

export const getIsUnacceptedTrainIncident = alert => {
  return (
    alert?.emergency_type?.name === TRAIN_DERAILMENT &&
    alert?.status?.name !== ACCEPTED
  );
};
